import React, { useMemo, useState, useEffect } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import { Download, Search, Filter, Plus, FileText, SquarePen } from "lucide-react";
import { get, post } from "../utils/api";
import AddDistributorForm from "./AddDistributorForm";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

const DistributorMaster = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

const downloadPdf = async (row) => {
    try {
      const distributor_id = row.distributor_sequence_number;
      if (!distributor_id) {
        toast.error("Distributor Sequence Number missing.");
        return;
      }
 
      console.log("Fetching contract details for:", distributor_id);
      const response = await post("/eSign/getDistributorContractDetails", {
        distributor_id: distributor_id,
      });
 
      if (response.data?.Success && response.data.Data) {
        const contractId = response.data.Data.amc_contractId;
 
        if (!contractId) {
          alert("Contract ID missing. Please complete e-sign first.");
          return;
        }
 
        const url = `https://d14wm9j0bgcvlw.cloudfront.net/DistributorInvoiceFiles/Esign/signed_${contractId}.pdf`;
 
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        alert(response.data?.Message || "Contract ID missing. Please complete e-sign first.");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Error occurred while fetching contract details.");
    }
  };


  const columns = useMemo(
    () => [
      {
        Header: "Action",
        accessor: "action",
        size: 260,
        Cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {/* EDIT BUTTON */}
            <button
              className="flex items-center justify-center p-[6px] w-[30px] h-[30px] text-indigo-500 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 hover:text-indigo-600 rounded-lg transition-all"
              onClick={async () => {
                const detailedData = await fetchDistributorById(row.original.id, row.original.distributor_sequence_number);
                if (detailedData) {
                  setSelectedDistributor(detailedData);
                  setShowAddForm(true);
                }
              }}
            >
              <SquarePen size={15} />
            </button>

            {/* DOWNLOAD BUTTON */}
            <button
              onClick={() => downloadPdf(row.original)}
              className="flex items-center justify-center gap-2 px-3 h-[30px] text-xs font-medium text-indigo-500 bg-indigo-50 border border-indigo-100 rounded-full hover:bg-indigo-100 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
              Signed Agreement
            </button>
          </div>
        ),
        disableSortBy: true,
      },
      // {
      //   Header: "ID",
      //   accessor: "id",
      //   size: 70,
      //   Cell: ({ value }) => <span className="font-bold text-gray-900">{value}</span>
      // },
      {
        Header: "Distributor Name",
        accessor: "distributor_name",
        size: 200,
        Cell: ({ value }) => <span className="font-medium text-gray-800 uppercase leading-tight">{value}</span>
      },
      {
        Header: "Fund Details",
        accessor: "fund_name",
        size: 250,
        Cell: ({ value }) => (
          <div className="truncate text-xs text-gray-500 font-medium" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "APRN Details",
        accessor: (row) => `${row.arn_code} | ${row.arn_expiry_date}`,
        size: 180,
        Cell: ({ row }) => (
          <div className="flex flex-col text-xs">
            <span className="font-bold text-indigo-600">{row.original.arn_code}</span>
            <span className="text-gray-400 font-medium">Expires: {row.original.arn_expiry_date}</span>
          </div>
        )
      },
      {
        Header: "PAN Number",
        accessor: "pan_number",
        size: 150,
        Cell: ({ value }) => <span className="font-bold text-gray-700 tracking-wider uppercase">{value}</span>
      },
      {
        Header: "Email",
        accessor: "email",
        size: 290,
        Cell: ({ value }) => (
          <span className="text-sm text-gray-700 lowercase break-all">
            {value || "-"}
          </span>
        )
      },
      {
        Header: "Contact Info",
        accessor: "spoc_name",
        size: 250,
        Cell: ({ row }) => (
          <div className="flex flex-col space-y-0.5 max-w-[230px]">
            <span className="text-sm font-bold text-gray-800 line-clamp-1 uppercase">{row.original.spoc_name}</span>
            <span className="text-[11px] text-gray-400 font-medium truncate italic lowercase">{row.original.email1}</span>
            <span className="text-[11px] text-gray-500 font-bold tracking-tight">{row.original.mobile1}</span>
          </div>
        )
      },
      // {
      //   Header: "ARN Status",
      //   accessor: "is_arn_active",
      //   size: 150,
      //   Cell: ({ value }) => {
      //     const statusText = value ? "Active" : "Inactive";
      //     const bgColor = value ? "bg-[#EBFFF3]" : "bg-[#F3F4F6]";
      //     const textColor = value ? "text-[#10B981]" : "text-[#6B7280]";
      //     const dotColor = value ? "bg-[#10B981]" : "bg-[#9CA3AF]";

      //     return (
      //       <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-bold w-fit mx-auto`}>
      //         <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      //         {statusText}
      //       </div>
      //     );
      //   },
      // },
      {
        Header: "Status",
        accessor: "status",
        size: 120,
        Cell: ({ value }) => {
          const statusText = value ? "Active" : "Inactive";
          const bgColor = value ? "bg-[#EBFFF3]" : "bg-[#F3F4F6]";
          const textColor = value ? "text-[#10B981]" : "text-[#6B7280]";
          const dotColor = value ? "bg-[#10B981]" : "bg-[#9CA3AF]";

          return (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-bold w-fit mx-auto`}>
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {statusText}
            </div>
          );
        },
      },
    ],
    []
  );

  const fetchDistributorById = async (id, sequenceNumber) => {
    try {
      const response = await post("/distributor/fetchDistributorCompleteDetails", {
        distributor_id: id,
        distributor_sequence_number: sequenceNumber,
      });

      if (response.data?.Success && response.data.Data) {
        console.log("✅ Fetched complete distributor data:", response.data.Data);
        return response.data.Data;
      } else {
        console.error("Failed to fetch distributor details:", response.data?.Message);
        toast.error("Failed to fetch distributor details");
      }
    } catch (err) {
      console.error("Error fetching distributor:", err);
      toast.error("Error fetching distributor details");
    }
    return null;
  };

  const fetchDistributors = async () => {
    try {
      const response = await get("/distributor/fetchDistributors");
      if (response.data?.Success && Array.isArray(response.data.Data)) {
        const transformed = response.data.Data.map((item) => {
          const uniqueFunds = item.products
            ? [
              ...new Set(
                item.products.map((p) => p.fund_name).filter(Boolean),
              ),
            ]
            : [];

          return {
            id: item.id,
            distributor_sequence_number: item.distributor_sequence_number,
            distributor_name: item.distributor_name,
            fund_name: uniqueFunds.join(", ") || item.fund_name || "-",
            arn_code: item.arn_code,
            arn_expiry_date: item.arn_expiry_date && moment(item.arn_expiry_date).isValid()
              ? moment(item.arn_expiry_date).format("DD-MM-YYYY")
              : "N/A",
            pan_number: item.pan_number,
            email: item.email || "",
            spoc_name: item.spoc_name || "",
            email1: item.email_1 || "",
            email2: item.email_2 || "",
            mobile1: item.mobile_1 || "",
            mobile2: item.mobile_2 || "",
            pin_code: item.pincode || "",
            is_arn_active: item.is_arn_active,
            status: item.is_active,
          };
        });
        setData(transformed);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!data || data.length === 0) {
      toast("No data to download");
      return;
    }

    const sortedData = [...data].sort((a, b) => a.id - b.id);
    const headers = Object.keys(sortedData[0]);
    const csvRows = [
      headers.join(","),
      ...sortedData.map((row) =>
        headers.map((field) => `"${row[field] ?? ""}"`).join(",")
      ),
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "distributor_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchDistributors();
  }, []);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [data, searchTerm]);

  const hasNoData = !loading && filteredData.length === 0;

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 w-full max-w-3xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, APRN code, PAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white h-[42px]"
            />
          </div>
          <button
            type="button"
            onClick={downloadCSV}
            className="flex items-center gap-2 text-gray-700 border border-gray-200 hover:bg-gray-50 bg-white h-[42px] px-4 rounded-lg font-medium shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-nowrap">Export To Excel</span>
          </button>
        </div>

        {/* <button
          className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-sm border-none h-[42px] px-5 rounded-lg transition-colors"
          onClick={() => {
            if (showAddForm) {
              setShowAddForm(false);
              setSelectedDistributor(null);
            } else {
              setSelectedDistributor(null);
              setShowAddForm(true);
            }
          }}
        >
          {showAddForm ? (
            <span className="font-medium text-sm">Back</span>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span className="font-medium text-sm text-nowrap">Add Distributor</span>
            </>
          )}
        </button> */}
      </div>

      {!showAddForm ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500 bg-white">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              Loading distributors...
            </div>
          ) : hasNoData ? (
            <div className="text-center py-20 px-6">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-300" />
              </div>
              <div className="text-gray-900 text-lg font-bold mb-2">
                No Results Found
              </div>
              <div className="text-gray-500 max-w-xs mx-auto">
                No distributors match your current search criteria. Try a different term.
              </div>
            </div>
          ) : (
            <ReusableTable columns={columns} data={filteredData} />
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6">
          <AddDistributorForm
            userId={2}
            distributorData={selectedDistributor}
            onBack={() => {
              setShowAddForm(false);
              setSelectedDistributor(null);
              fetchDistributors();
            }}
          />
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DistributorMaster;
